/**
 * Shared helpers for MenuGenerator active/open state from the current URL.
 */

export const normalizeMenuPath = (path = "") => {
    if (!path || typeof path !== "string") {
        return "";
    }

    const withoutQuery = path.split("?")[0].split("#")[0];
    const trimmed = withoutQuery.replace(/\/+$/, "");

    return trimmed || "/";
};

const pathSegments = (path) =>
    normalizeMenuPath(path).split("/").filter(Boolean);

/**
 * True when currentPath equals menuPath, is nested under it, or matches a
 * React-Router-style pattern (`:param` segments).
 * Optional matchPaths on a menu item can widen matching without changing the link.
 */
export const pathMatchesMenuPath = (currentPath, menuPath) => {
    const current = normalizeMenuPath(currentPath);
    const menu = normalizeMenuPath(menuPath);

    if (!menu || menu === "/") {
        return false;
    }

    if (current === menu || current.startsWith(`${menu}/`)) {
        return true;
    }

    if (!menu.includes(":")) {
        return false;
    }

    const menuParts = pathSegments(menu);
    const currentParts = pathSegments(current);

    if (currentParts.length < menuParts.length) {
        return false;
    }

    for (let i = 0; i < menuParts.length; i += 1) {
        const menuPart = menuParts[i];
        const currentPart = currentParts[i];

        if (menuPart.startsWith(":")) {
            continue;
        }

        if (menuPart !== currentPart) {
            return false;
        }
    }

    return true;
};

const getItemMatchPaths = (item = {}) => {
    const paths = [];

    if (item.path) {
        paths.push(item.path);
    }

    if (Array.isArray(item.matchPaths)) {
        paths.push(...item.matchPaths);
    }

    return paths.filter(Boolean);
};

/**
 * Finds the best (longest path) matching menu node for the current location.
 * Returns { indexes: number[], score: number } or null.
 */
export const findActiveMenuMatch = (menuItems = [], currentPath) => {
    let best = null;

    const walk = (items, parentIndexes = []) => {
        if (!Array.isArray(items)) {
            return;
        }

        items.forEach((item, index) => {
            const indexes = [...parentIndexes, index];

            getItemMatchPaths(item).forEach((candidatePath) => {
                if (!pathMatchesMenuPath(currentPath, candidatePath)) {
                    return;
                }

                const score = normalizeMenuPath(candidatePath).length;

                if (!best || score > best.score) {
                    best = { indexes, score };
                }
            });

            if (item?.items?.length) {
                walk(item.items, indexes);
            }
        });
    };

    walk(menuItems);

    return best;
};

/**
 * Vertical menu uses keys like "0", "0--1", "0--1--2".
 */
export const getActiveMenuIndexKey = (match) => {
    if (!match?.indexes?.length) {
        return "-1";
    }

    return match.indexes.join("--");
};

export const isMenuIndexActive = (activeKey, ...parts) => {
    if (!activeKey || activeKey === "-1" || !parts.length) {
        return false;
    }

    const prefix = parts.join("--");

    return activeKey === prefix || activeKey.startsWith(`${prefix}--`);
};
